/**
 * @file BlendExportModal.js
 * @version 1.7.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-13
 * @modified 2026-10-02
 * @description Turn the blend on screen into a document — download, print, or
 *              email.
 *
 *              Four document types, and the choice matters more than usual: the
 *              supplier specification carries no costs at all, so it is the one
 *              that can safely leave the business. That is enforced server-side
 *              (a separate payload builder plus a leak assertion), but the modal
 *              says which is which so nobody has to remember.
 *
 *              Follows FormulationExportModal's three-mode card layout and
 *              reuses its CSS, so the two exports look and behave the same.
 *              "What to do with it" is in the footer (ExportModeFooter), beside
 *              the button that does it; the email fields it reveals stay at the
 *              foot of the options column, and take the focus when they appear
 *              (revealOnMount).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import ExportPreview from './ExportPreview';
import ExportLayoutOptions from './ExportLayoutOptions';
import { blendReferenceData, periodOptionsFrom } from '../utils/blendReferenceData';
import Input from './Input';
import Textarea from './Textarea';
import Dropdown from './Dropdown';
import { BLEND_TYPE_OPTIONS } from '../utils/blendTypes';
import DateInput from './DateInput';
import SearchableDropdown from './SearchableDropdown';
import SenderSelect from './SenderSelect';
import TickCheckbox from './TickCheckbox';
import ExportModeFooter, { EXPORT_MODES, revealOnMount } from './ExportModeFooter';
import './Formulations.css';
import './BlendCalculator.css';

const DOC_TYPES = [
  {
    key: 'quote', icon: 'request_quote', label: 'Customer quote',
    hint: 'Recipe, analysis and a delivered price per basis, with the 24-hour validity note'
  },
  {
    key: 'spec', icon: 'factory', label: 'Supplier specification',
    hint: 'What to make. No costs of any kind — safe to send to the mill'
  },
  {
    key: 'comparison', icon: 'compare_arrows', label: 'Comparison',
    hint: 'Both blends side by side. Everything beyond the recipe and analysis is opt-in',
    needsCompare: true
  }
  // The internal working sheet is gone: it was a PDF of what staff can already
  // see on screen in Saved Blends.
];

/*
 * What a comparison sheet may carry beyond the recipe and the analysis.
 *
 * All OFF by default. A comparison is most often put in front of a customer,
 * and our cost per tonne is the one figure on it that is nobody's business but
 * ours — so it is opted INTO. A default the other way round leaks a margin
 * every time somebody forgets to untick a box.
 */
const COMPARISON_INCLUDES = [
  { key: 'costs', label: 'Our cost per tonne',
    hint: 'Raw material and delivered cost for each blend' },
  { key: 'salePrice', label: 'Sale price',
    hint: 'What each blend is being sold at' },
  { key: 'customer', label: 'Customer details',
    hint: 'Customer name and delivery address in the header' },
  { key: 'supplier', label: 'Material suppliers',
    hint: 'The supplier code against each raw material' }
];

// The workbook's own wording for these two, as free text. They are a fixed set
// in practice, so a dropdown stops three spellings of "Blown" reaching a quote.
const PACKAGING = [
  { value: '', label: 'Select…' },
  { value: 'Bulk', label: 'Bulk' },
  { value: 'Totes', label: 'Totes' }
];

const DELIVERY_TERMS = [
  { value: '', label: 'Select…' },
  { value: 'Delivered', label: 'Delivered' },
  { value: 'Collected', label: 'Collected' },
  { value: 'Tipped', label: 'Tipped' },
  { value: 'Blown', label: 'Blown' }
];

function ExportBody({
  docType, setDocType, mode, header, setHeaderField,
  columns, bands, priceColumnKeys, setPriceColumnKeys, haulageBandKey, setHaulageBandKey,
  customerOptions, customerId, onPickCustomer, customersLoading, addressSource,
  periodOptions, periodMode, onPickPeriod, periodFrom, setPeriodFrom, periodTo, setPeriodTo,
  to, setTo, subject, setSubject, message, setMessage, error, blocked, warning,
  user, canUseFeed, sendAs, onSendAs, sendingToFeed,
  hasCompare, include, setIncludeField,
  layout, setLayoutField, previewPayload
}) {
  // Comparison is offered only while a comparison is actually open.
  const docTypes = useMemo(
    () => DOC_TYPES.filter((d) => !d.needsCompare || hasCompare), [hasCompare]);

  const bandOptions = useMemo(
    () => [{ value: '', label: 'Raw material (ex works)' },
           ...bands.map((b) => ({ value: b.key, label: b.label }))], [bands]);

  return (
    <div className="blend-export-split">
      <div className="blend-export-body">
      {error && <div className="formulation-error">{error}</div>}
      {blocked && (
        <div className="blend-warning">
          <span className="material-symbols-rounded">warning</span>
          <span>{blocked}</span>
        </div>
      )}
      {/* Advisory, not a gate: it explains what the document will say about
          itself, and the export goes ahead. */}
      {!blocked && warning && (
        <div className="blend-warning blend-warning-advisory">
          <span className="material-symbols-rounded">info</span>
          <span>{warning}</span>
        </div>
      )}

      <div className="fm-export-step">Which document</div>
      <div className="fm-export-modes is-list">
        {docTypes.map((d) => (
          <button key={d.key} type="button"
                  className={`fm-export-mode${docType === d.key ? ' active' : ''}`}
                  onClick={() => setDocType(d.key)}>
            <span className="material-symbols-rounded">{d.icon}</span>
            <span className="fm-export-mode-label">{d.label}</span>
            <span className="fm-export-mode-hint">{d.hint}</span>
          </button>
        ))}

      </div>
      <ExportLayoutOptions
        orientation={layout.orientation}
        onOrientation={(v) => setLayoutField('orientation', v)}
        fit={layout.fit}
        onFit={(v) => setLayoutField('fit', v)}
        scale={layout.scale}
        onScale={(v) => setLayoutField('scale', v)}
      />

      {/* Opt-ins, comparison only. Toggles rather than tick boxes because each
          one turns a whole section of the document on. */}
      {docType === 'comparison' && (
        <div className="fm-export-field">
          <label>Include on the sheet</label>
          <div className="blend-export-includes">
            {COMPARISON_INCLUDES.map((opt) => (
              <div className="blend-export-include" key={opt.key}>
                <div className="checkbox-container">
                  <div className="check light">
                    <input id={`bx-inc-${opt.key}`} type="checkbox"
                           checked={!!include[opt.key]}
                           onChange={(e) => setIncludeField(opt.key, e.target.checked)} />
                    <label htmlFor={`bx-inc-${opt.key}`}></label>
                  </div>
                </div>
                <div className="blend-export-include-text">
                  <span className="blend-export-include-label">{opt.label}</span>
                  <span className="blend-export-include-hint">{opt.hint}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* A supplier specification has no price, so the price controls and the
          quote header would be meaningless on it. */}
      {docType !== 'spec' && (
        <div className="blend-export-row">
          <div className="fm-export-field">
            <label>Price column</label>
            {/* Several bases can be quoted together — Spot alongside a
                contract period is a normal thing to put in front of a
                customer. The PDF lists one price row per basis. */}
            {/* TickCheckbox, per the project rule — and because .bdp-checks
                is styled under .blend-details-panel, so the raw boxes this
                used to render were never styled at all in here. */}
            <div className="blend-export-columns">
              {columns.map((c) => (
                <TickCheckbox
                  key={c.key}
                  id={`bx-col-${c.key}`}
                  checked={priceColumnKeys.includes(c.key)}
                  label={c.label}
                  onChange={() => setPriceColumnKeys(
                    priceColumnKeys.includes(c.key)
                      ? priceColumnKeys.filter((k) => k !== c.key)
                      : [...priceColumnKeys, c.key])}
                />
              ))}
            </div>
          </div>
          {docType === 'quote' && (
            <div className="fm-export-field">
              <label>Delivery band</label>
              <Dropdown options={bandOptions} value={haulageBandKey} onChange={setHaulageBandKey} />
            </div>
          )}
        </div>
      )}

      {docType === 'quote' && (
        <details className="blend-export-details" open>
          <summary>Quote details</summary>
          <div className="blend-export-grid">
            <div className="fm-export-field">
              <label htmlFor="bx-blendName">Blend name *</label>
              <Input id="bx-blendName" type="text" value={header.blendName || ''}
                     placeholder="Name this blend for the document"
                     onChange={(e) => setHeaderField('blendName', e.target.value)} />
              {!String(header.blendName || '').trim() && (
                <span className="blend-field-hint warn">
                  Required — it's the heading on the document.
                </span>
              )}
            </div>
            <div className="fm-export-field">
              <label>Blend type</label>
              <Dropdown
                options={BLEND_TYPE_OPTIONS}
                value={header.blendType || ''}
                onChange={(v) => setHeaderField('blendType', v)}
              />
            </div>

            <div className="fm-export-field blend-export-wide">
              <label>Customer</label>
              <SearchableDropdown
                options={customerOptions}
                value={customerId}
                onChange={onPickCustomer}
                placeholder={customersLoading ? 'Loading customers…' : 'Search customers…'}
                searchPlaceholder="Name or account number…"
              />
            </div>

            {/* Auto-filled from the chosen customer, and editable. Which address
                it came from is stated: delivering to an invoice address because
                no delivery address is on file is something to notice before the
                quote goes out, not after. */}
            <div className="fm-export-field blend-export-wide">
              <label htmlFor="bx-address">Delivery address</label>
              <Input id="bx-address" type="text" value={header.deliveryAddressLine || ''}
                     placeholder={customerId ? 'No address on file for this customer' : 'Pick a customer, or type one'}
                     onChange={(e) => setHeaderField('deliveryAddressLine', e.target.value)} />
              {addressSource && (
                <span className={`blend-field-hint${addressSource === 'invoice' ? ' warn' : ''}`}>
                  {addressSource === 'delivery'
                    ? 'From the customer’s delivery address.'
                    : 'No delivery address on file — this is the INVOICE address.'}
                </span>
              )}
            </div>

            <div className="fm-export-field">
              <label htmlFor="bx-tonnes">Tonnes contracted</label>
              <Input id="bx-tonnes" type="number" inputMode="decimal"
                     value={header.tonnesContracted || ''}
                     onChange={(e) => setHeaderField('tonnesContracted', e.target.value)} />
            </div>
            <div className="fm-export-field">
              <label>Delivery method</label>
              <Dropdown options={PACKAGING} value={header.packaging || ''}
                        onChange={(v) => setHeaderField('packaging', v)} />
            </div>
            <div className="fm-export-field">
              <label>Delivery</label>
              <Dropdown options={DELIVERY_TERMS} value={header.deliveryTerms || ''}
                        onChange={(v) => setHeaderField('deliveryTerms', v)} />
            </div>
            <div className="fm-export-field">
              <label>Contract period</label>
              <Dropdown options={periodOptions} value={periodMode}
                        onChange={onPickPeriod} />
            </div>

            {/* Custom dates are the escape hatch for a contract that doesn't sit
                on a season boundary. */}
            {periodMode === 'custom' && (
              <>
                <div className="fm-export-field">
                  <label>From</label>
                  <DateInput value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} />
                </div>
                <div className="fm-export-field">
                  <label>To</label>
                  <DateInput value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} />
                </div>
              </>
            )}

            <div className="fm-export-field blend-export-wide">
              <label htmlFor="bx-notes">Notes</label>
              <Textarea id="bx-notes" rows={2} value={header.notes || ''}
                        onChange={(e) => setHeaderField('notes', e.target.value)} />
            </div>
          </div>
        </details>
      )}

      {/* What to do with it is picked in the footer; the fields Email needs
          stay here, where there is room for them to scroll. */}
      {mode === 'email' && (
        <div className="fm-export-email" ref={revealOnMount}>
          {/* Only offered to those who may send from feed@ (Standard Users and
              admins); everyone else sends from their own address, and the
              server holds to that whatever the page sends. */}
          {canUseFeed && (
            <div className="fm-export-field">
              <label>Send from</label>
              <SenderSelect user={user} value={sendingToFeed ? 'me' : sendAs}
                            only={['me', 'feed']} onChange={onSendAs}
                            disabled={sendingToFeed} />
              {sendingToFeed && (
                <p className="table-export-note">Sent from your own address — feed@ does not email itself.</p>
              )}
            </div>
          )}
          <div className="fm-export-field">
            <label htmlFor="bx-to">To</label>
            <Input id="bx-to" type="email" value={to} icon="mail"
                   placeholder="Leave blank to send to yourself"
                   onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="fm-export-field">
            <label htmlFor="bx-subject">Subject</label>
            <Input id="bx-subject" type="text" value={subject}
                   placeholder="Default subject will be used if blank"
                   onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="fm-export-field">
            <label htmlFor="bx-message">Message</label>
            <Textarea id="bx-message" rows={3} value={message}
                      placeholder="Optional covering message…"
                      onChange={(e) => setMessage(e.target.value)} />
          </div>
        </div>
      )}
      </div>

      {/* The document itself, beside the choices that shape it. Same html the
          PDF is built from — see ExportPreview. */}
      <ExportPreview
        endpoint="/api/blends/export/preview"
        payload={previewPayload}
        disabled={!!blocked}
        note={blocked ? 'Fix the blend to preview it' : ''}
      />
    </div>
  );
}

export default function BlendExportModal({
  blend, compareBlend = null, columns = [], bands = [], asOf, summary,
  // key -> display name, so an unpriced material is named in the warning
  // rather than shown as its key. summary carries no such map.
  materialNames = null,
  // Only used to label "your address" in the sender picker; without it the
  // picker still works and says "My address (you)".
  user = null,
  onClose, onDone
}) {
  const [docType, setDocType] = useState('quote');
  /*
   * Comparison opt-ins. Every one starts false — see COMPARISON_INCLUDES. Held
   * here rather than in the body so switching document type and back does not
   * quietly re-tick something the user turned off.
   */
  const [include, setInclude] = useState(
    { costs: false, salePrice: false, customer: false, supplier: false });

  /*
   * How the page is laid out. A comparison opens landscape because it is wide
   * by nature; everything else opens portrait, which is what these documents
   * were designed against.
   */
  const [layout, setLayout] = useState(
    { orientation: 'portrait', fit: 'width', scale: 1 });
  useEffect(() => {
    setLayout((l) => ({ ...l,
      orientation: docType === 'comparison' ? 'landscape' : 'portrait' }));
  }, [docType]);
  const setLayoutField = useCallback(
    (key, value) => setLayout((prev) => ({ ...prev, [key]: value })), []);
  const setIncludeField = useCallback(
    (key, value) => setInclude((prev) => ({ ...prev, [key]: value })), []);
  const [mode, setMode] = useState('download');
  // Seeded from what the blend was set up with, so the export does not ask
  // again for something already chosen in the calculator.
  const [priceColumnKeys, setPriceColumnKeys] = useState(
    (blend?.price_column_keys?.length ? blend.price_column_keys : [columns[0]?.key]).filter(Boolean));
  // Raw material by default, not a delivered band. A quote's starting point is
  // what the mix costs; the haulage band is a decision the quoter makes. The
  // server already handles a null band — buildQuoteSheet falls back to
  // rawMaterialCost and labels it "Ex works" — it just had no way to say so,
  // since the dropdown was fed only real bands.
  const [haulageBandKey, setHaulageBandKey] = useState(blend?.haulage_band_key || '');
  // Pre-filled from the saved blend when one is open; otherwise left empty and
  // marked required, rather than seeded with a placeholder like "Blend" that
  // would print onto a customer quote if nobody noticed it.
  /*
   * Seeded from everything the calculator already knows.
   *
   * The calculator has been handing all of this over for some time — customer,
   * delivery address, packaging, terms, tonnage, period, notes — and the modal
   * was reading the name and type and dropping the rest, so a user who had
   * filled the details panel in was asked for the same answers again. Anything
   * absent still falls back to empty, which is the old behaviour for a blend
   * that was never given details.
   */
  const [header, setHeader] = useState({
    blendName: blend?.name || '',
    blendType: blend?.blend_type || '',
    deliveryAddressLine: blend?.delivery_address_line || '',
    packaging: blend?.packaging || '',
    deliveryTerms: blend?.delivery_terms || '',
    tonnesContracted: blend?.tonnes_contracted || '',
    periodOfContract: blend?.period_of_contract || '',
    notes: blend?.notes || ''
  });
  const [to, setTo] = useState('');
  // The sender. Standard Users may send from feed@, and it is their default
  // for an address outside the company; everyone sends from their own address
  // otherwise. Once someone picks, their choice stands over the default.
  const [canUseFeed, setCanUseFeed] = useState(false);
  const [pickedSender, setPickedSender] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/blends/export/sender-options`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (alive) setCanUseFeed(!!d.canUseFeed); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const recipient = to.trim().toLowerCase();
  const sendingToFeed = recipient === 'feed@excel-agri.com';
  const external = !!recipient && !/@excel-agri\.com$/i.test(recipient);
  const sendAs = pickedSender || (canUseFeed && external ? 'feed' : 'me');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  // The customer list keys on a numeric id; the calculator stores it as a
  // string in persisted state, so it is coerced here rather than failing to
  // match and showing an empty picker over a chosen customer.
  const [customerId, setCustomerId] = useState(
    blend?.customer_account_id ? Number(blend.customer_account_id) || null : null);
  // Not re-derived from the customer: the address already came across, and the
  // user may have edited it. addressSource stays null so the "this is the
  // INVOICE address" note only appears for an address this modal filled in.
  const [addressSource, setAddressSource] = useState(null);   // 'delivery' | 'invoice'
  const [periods, setPeriods] = useState([]);
  const [periodMode, setPeriodMode] = useState(blend?.period_of_contract || '');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');

  // Customers and contract periods. Cached for the tab's lifetime rather than
  // refetched per open: the customer list is ~1.6 MB over 1100 accounts and
  // takes a second or two to arrive, which on a reopen is dead time in front of
  // an empty dropdown. Reference data that changes daily at most.
  useEffect(() => {
    let cancelled = false;
    blendReferenceData().then(({ customers: c, periods: p }) => {
      if (cancelled) return;
      setCustomers(c);
      setPeriods(p);
      setCustomersLoading(false);
    }).catch(() => { if (!cancelled) setCustomersLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const customerOptions = useMemo(() => customers.map((c) => ({
    value: c.id,
    label: c.company_name,
    badge: c.account_no || undefined,
    searchText: `${c.company_name} ${c.account_no || ''}`
  })), [customers]);

  /**
   * Pre-fill the delivery address from the chosen customer.
   *
   * Delivery address wins; the invoice address is the fallback, and the fact
   * that it IS the invoice address is surfaced rather than quietly used — a
   * quote that promises delivery to a billing address is worth catching before
   * it is sent.
   */
  const onPickCustomer = useCallback((id) => {
    setCustomerId(id);
    const c = customers.find((x) => x.id === id);
    if (!c) { setAddressSource(null); return; }

    const compose = (line, city, postcode) =>
      [line, city, postcode].map((v) => (v || '').trim()).filter(Boolean).join(', ');

    const delivery = compose(c.delivery_line1, c.delivery_city, c.delivery_postcode);
    const invoice = compose(c.invoice_line1, c.invoice_city, c.invoice_postcode);

    setHeaderField('customerName', c.company_name);
    if (delivery) {
      setHeaderField('deliveryAddressLine', delivery);
      setAddressSource('delivery');
    } else if (invoice) {
      setHeaderField('deliveryAddressLine', invoice);
      setAddressSource('invoice');
    } else {
      setHeaderField('deliveryAddressLine', '');
      setAddressSource(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  /**
   * Contract period: the current season, the one before, and the next two,
   * derived from the defined contract periods rather than typed as free text.
   * "Custom dates" is the escape hatch for a contract that doesn't sit on a
   * season boundary.
   */
  const periodOptions = useMemo(() => periodOptionsFrom(periods, { includeCustom: true }), [periods]);

  const onPickPeriod = useCallback((value) => {
    setPeriodMode(value);
    if (value !== 'custom') setHeaderField('periodOfContract', value);
  }, []);

  // Custom dates compose into the same single string the template prints.
  useEffect(() => {
    // Only the custom branch composes a string; a seeded period name is left
    // exactly as the calculator set it.
    if (periodMode !== 'custom') return;
    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB',
      { day: '2-digit', month: 'short', year: 'numeric' }) : '');
    const text = periodFrom && periodTo
      ? `${fmtDate(periodFrom)} – ${fmtDate(periodTo)}`
      : (periodFrom ? `From ${fmtDate(periodFrom)}` : '');
    setHeaderField('periodOfContract', text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodMode, periodFrom, periodTo]);

  const setHeaderField = (key, value) => setHeader((prev) => ({ ...prev, [key]: value }));

  // Say why the export will be refused before the user tries it. The server
  // enforces both independently — this is only so the reason arrives early.
  /*
   * A blend that does not total 100 is genuinely wrong, and every figure on the
   * document is calculated against 100 — so that one still stops the export.
   */
  const blocked = useMemo(() => {
    if (!summary) return null;
    if (!summary.isBalanced) {
      return `This blend totals ${summary.total.toFixed(2)}%. Balance it to 100% before exporting — `
        + 'every figure is calculated against 100, so the analysis and cost are understated.';
    }
    return null;
  }, [summary]);

  /*
   * Unpriced materials, which used to stop the export and now do not.
   *
   * A blend can legitimately contain something nobody has quoted yet — a new
   * ingredient, a material between price lists — and refusing to produce the
   * document made that a dead end with no way round it. The document now says
   * so instead: the rows are marked and a notice at the top explains what the
   * price excludes. This warning is the same fact, said before it is sent
   * rather than after.
   */
  const unpricedWarning = useMemo(() => {
    if (!summary || docType === 'spec') return null;
    const keys = [...new Set(priceColumnKeys.flatMap((k) => summary.pricing?.[k]?.missing || []))];
    if (!keys.length) return null;
    const names = keys.map((k) => materialNames?.[k] || k);
    const shows = docType === 'comparison' ? !!include.costs || !!include.salePrice : true;
    const one = names.length === 1;
    const opening = one
      ? `One material has no price in this period: ${names[0]}.`
      : `${names.length} materials have no price in this period: ${names.join(', ')}.`;
    const marked = one ? 'It will be marked' : 'They will be marked';
    return shows
      ? `${opening} ${marked} on the document, and the price shown will EXCLUDE ${one ? 'it' : 'them'}.`
      : `${opening} ${marked} on the document. No price is shown on it, so nothing is affected.`;
  }, [summary, priceColumnKeys, docType, include, materialNames]);

  /*
   * What the document is made of, without how it is delivered.
   *
   * The preview and the export post the same object — the preview simply omits
   * mode and the recipient — so what is on screen cannot be a document with
   * different contents from the one that prints.
   */
  const documentPayload = useMemo(() => ({
    docType, as_of: asOf, priceColumnKeys, haulageBandKey, layout,
    quoteHeader: { ...header, blendName: header.blendName || blend?.name },
    // A comparison sends BOTH sides, live first: the sheet's delta column
    // reads as second-minus-first, so the reference belongs second.
    blends: docType === 'comparison' && compareBlend
      ? [
          {
            name: header.blendName || blend?.name || 'Blend',
            blend_type: header.blendType || '',
            material_values: blend.material_values,
            sale_price: blend.sale_price ?? null
          },
          {
            name: compareBlend.name || 'Reference',
            blend_type: compareBlend.blend_type || '',
            material_values: compareBlend.material_values,
            sale_price: compareBlend.sale_price ?? null
          }
        ]
      : [{
          name: header.blendName || blend?.name || 'Blend',
          blend_type: header.blendType || '',
          material_values: blend.material_values,
          sale_price: blend.sale_price ?? null
        }],
    // Ignored by the server for every other document type.
    include
  }), [docType, asOf, priceColumnKeys, haulageBandKey, layout, header, blend, compareBlend, include]);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/blends/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...documentPayload,
          mode, to, subject, message,
          ...(mode === 'email' ? { sendAs: sendingToFeed ? 'me' : sendAs } : {})
        })
      });

      if (mode === 'download') {
        if (!res.ok) throw new Error((await res.json()).error || 'Export failed');
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${docType}-${(header.blendName || 'blend').replace(/[^a-z0-9]+/gi, '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Revoking synchronously cancels the download in some browsers.
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        onDone('Downloaded');
        return;
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Export failed');
      onDone(data.message);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const verb = mode === 'download' ? 'Download' : mode === 'print' ? 'Print' : 'Send';
  const icon = mode === 'download' ? 'download' : mode === 'print' ? 'print' : 'send';

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Export this blend"
      size="lg"
      className="formulation-export blend-export"
      footer={
        <ExportModeFooter modes={EXPORT_MODES} mode={mode} onMode={setMode} busy={busy}>
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={busy}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-round" onClick={submit} disabled={busy || !!blocked}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${busy ? ' spinning' : ''}`}>
                  {busy ? 'progress_activity' : icon}
                </span>
              </div>
              <span className="btn-label">{busy ? 'Working…' : verb}</span>
            </button>
          </div>
        </ExportModeFooter>
      }
    >
      <ExportBody
        docType={docType} setDocType={setDocType}
        mode={mode}
        header={header} setHeaderField={setHeaderField}
        columns={columns} bands={bands}
        priceColumnKeys={priceColumnKeys} setPriceColumnKeys={setPriceColumnKeys}
        haulageBandKey={haulageBandKey} setHaulageBandKey={setHaulageBandKey}
        customerOptions={customerOptions} customerId={customerId}
        onPickCustomer={onPickCustomer} customersLoading={customersLoading}
        addressSource={addressSource}
        periodOptions={periodOptions} periodMode={periodMode} onPickPeriod={onPickPeriod}
        periodFrom={periodFrom} setPeriodFrom={setPeriodFrom}
        periodTo={periodTo} setPeriodTo={setPeriodTo}
        to={to} setTo={setTo}
        user={user} canUseFeed={canUseFeed} sendAs={sendAs} onSendAs={setPickedSender}
        sendingToFeed={sendingToFeed}
        subject={subject} setSubject={setSubject}
        message={message} setMessage={setMessage}
        hasCompare={!!compareBlend}
        include={include} setIncludeField={setIncludeField}
        layout={layout} setLayoutField={setLayoutField}
        previewPayload={documentPayload}
        error={error} blocked={blocked} warning={unpricedWarning}
      />
    </Modal>
  );
}
